import { useCommentLayout } from 'features/libra/comments/CommentLayoutProvider'
import { useStepNavigation } from 'features/libra/StepNavigationContext'
import { WorkflowStepContainer } from 'features/libra/WorkflowStepContainer'
import { FC } from 'react'
import styled from 'styled-components'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  overflow: auto;
  padding: 0 1rem;
`

const StepTitleBar = styled.div`
  flex: 0 0 auto;
  padding: 0.75rem 0 0.5rem;
  margin-bottom: 0.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
`

const StepTitle = styled.h2`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
`

const Empty = styled.div`
  padding: 1rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

/**
 * Generic workspace panel that renders the workflow's active step.
 *
 * Two upstream context requirements:
 *  - `TicketStepContextProvider` keyed off the active step, so step
 *    components can call `useTicketStepContext()`.
 *  - `CommentLayoutProvider`, so this component can register its scrollable
 *    `Container` as the comment-anchor scroll target. Without this, the
 *    sidebar's anchor positions stay empty in the N-panel branch.
 *
 * Both are mounted by `LibraView` above the N-panel render path.
 */
export const Component: FC = () => {
  const { activeStep } = useStepNavigation()
  const { setScrollContainerRef } = useCommentLayout()
  if (!activeStep) {
    return (
      <Container ref={setScrollContainerRef}>
        <Empty>No steps defined for this workflow.</Empty>
      </Container>
    )
  }
  return (
    <Container ref={setScrollContainerRef}>
      <StepTitleBar>
        <StepTitle>{activeStep.name}</StepTitle>
      </StepTitleBar>
      <WorkflowStepContainer
        componentName={activeStep.component}
        config={activeStep.config}
      />
    </Container>
  )
}
